import React, { useEffect } from "react";
import { Button, message, Modal } from "antd";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import "react-date-range/dist/styles.css"; // main style file
import DateRange from "../../../components/date-range";
import useRequest from "../../../components/hooks/use-request";
import DatePicker from "../../../components/date-picker";

const RestrictionModal = ({
  open,
  onCancel,
  account,
  refetchAccount,
  restrictionType,
  restriction,
  refetchRestriction,
}: any) => {
  const userToken = localStorage.getItem("token");

  const { makeRequest, loading } = useRequest(
    `/users/${account?.id}/restrict-wallet`,
    "POST",
    {
      Authorization: `Bearer ${userToken}`,
    }
  );

  const defaultValues = {
    expires: "",
    amount: 0,
    reason: "",
  };

  const schema = z.object({
    amount: z
      .number()
      .min(1, { message: "Amount is required" })
      .refine((val) => Number(val) <= account?.balance, {
        message: "Amount cannot be greater than wallet balance",
      }),
    reason: z.string().min(1, { message: "Reason is required" }),
    expires: z.string().min(1, { message: "Duration is required" }),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(schema),
    defaultValues,
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, reset]);

  const onSubmit = async (data: any) => {
    const payload = {
      ...data,
      notifyUser: true,
      type: restrictionType,
    };

    const [response] = await makeRequest(payload);
    if (response?.status === true) {
      message.success(response?.message || "Restriction successful");
      reset(defaultValues);
      refetchAccount();
      refetchRestriction();
      onCancel();
    } else {
      message.error(response?.message);
    }
  };

  useEffect(() => {
    if (restrictionType === "full") {
      reset({
        ...defaultValues,
        amount: Number(account?.balance),
      });
    }
  }, [restrictionType, account?.balance, reset]);

  const handleCancel = () => {
    reset(defaultValues);
    onCancel();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      centered
      className="flex justify-center items-center"
    >
      <div>
        <p className="text-sm font-semibold">
          {restrictionType === "partial"
            ? "Partial restriction"
            : "Full restriction"}
        </p>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="mt-4 flex flex-col gap-4"
        >
          <Controller
            control={control}
            name="amount"
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <label htmlFor="amount">Restricted amount</label>
                <div className="border border-[#D0D5DD] rounded-lg p-2 flex items-center gap-2 w-full">
                  <p className="text-sm font-medium">₦</p>
                  <input
                    type="text"
                    id="amount"
                    disabled={restrictionType === "full"}
                    value={
                      restrictionType === "partial"
                        ? field.value
                        : account?.balance
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      const formattedValue = value.replace(/[^0-9]/g, "");
                      field.onChange(Number(formattedValue));
                    }}
                    className="w-full outline-none"
                  />
                </div>
                {errors.amount && (
                  <p className="text-xs text-red-500">
                    {errors.amount?.message}
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <p className="text-xs font-medium">
                    Wallet Balance: ₦{account?.balance}
                  </p>
                  {restriction?.id && (
                    <p className="text-xs font-medium">
                      {restriction?.type === "partial"
                        ? `₦${restriction?.amount}`
                        : "Fully restricted"}
                    </p>
                  )}
                </div>
              </div>
            )}
          />
          <Controller
            control={control}
            name="reason"
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <label htmlFor="reason">
                  Enter a detailed reason for restriction
                </label>
                <textarea
                  rows={4}
                  id="reason"
                  {...field}
                  className="border border-[#D0D5DD] rounded-lg p-2 h-24 resize-none"
                />
                {errors.reason && (
                  <p className="text-xs text-red-500">
                    {errors.reason?.message}
                  </p>
                )}
              </div>
            )}
          />
          <Controller
            control={control}
            name="expires"
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <DatePicker
                  value={field.value ? new Date(field.value) : undefined}
                  onChange={field.onChange}
                  placeholder="Set duration"
                />
                {errors.expires && (
                  <p className="text-xs text-red-500">
                    {errors.expires?.message}
                  </p>
                )}
              </div>
            )}
          />
          <Button
            type="primary"
            htmlType="submit"
            className="w-full hover:w-full hover:bg-primary hover:text-white disabled:opacity-80 disabled:cursor-not-allowed disabled:text-white"
            disabled={loading || !isValid}
            loading={loading}
          >
            Submit
          </Button>
        </form>
      </div>
    </Modal>
  );
};

export default RestrictionModal;
